/**
 * TagSelector Component
 *
 * A component for selecting and managing tags on content items.
 * Displays tags grouped by category with colored chips.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Typography,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider,
  Button,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search,
  Add,
  LocalOffer,
  AutoAwesome,
  AddCircleOutline,
} from '@mui/icons-material';
import { TagService, type Tag } from '../../api';

interface TagSelectorProps {
  contentId: string;
  selectedTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
  compact?: boolean;
  showSuggestions?: boolean;
}

export default function TagSelector({
  contentId,
  selectedTags,
  onTagsChange,
  compact = false,
  showSuggestions = true,
}: TagSelectorProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [groupedTags, setGroupedTags] = useState<Record<string, Tag[]>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Tag[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inline tag creation state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagCategory, setNewTagCategory] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);

  // Default categories for new tags
  const TAG_CATEGORIES = ['Mood', 'Tempo', 'Era', 'Use', 'Vocal', 'Energy', 'Style', 'Season', 'Type', 'Genre', 'Custom'];

  // Default colors for categories
  const CATEGORY_COLORS: Record<string, string> = {
    Mood: '#f59e0b',
    Tempo: '#3b82f6',
    Era: '#8b5cf6',
    Use: '#10b981',
    Vocal: '#ec4899',
    Energy: '#ef4444',
    Style: '#6366f1',
    Season: '#14b8a6',
    Type: '#f97316',
    Genre: '#84cc16',
    Custom: '#6b7280',
  };

  // Load all available tags
  useEffect(() => {
    const loadTags = async () => {
      setLoading(true);
      try {
        const data = await TagService.getTags();
        setAllTags(data.tags);
        setGroupedTags(data.grouped);
      } catch (err) {
        setError('Failed to load tags');
      } finally {
        setLoading(false);
      }
    };
    loadTags();
  }, []);

  // Load suggestions when popover opens
  useEffect(() => {
    if (anchorEl && showSuggestions && contentId) {
      const loadSuggestions = async () => {
        setLoadingSuggestions(true);
        try {
          const data = await TagService.getSuggestions(contentId);
          setSuggestions(data);
        } catch (err) {
          // Silently fail - suggestions are optional
        } finally {
          setLoadingSuggestions(false);
        }
      };
      loadSuggestions();
    }
  }, [anchorEl, contentId, showSuggestions]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSearchQuery('');
    setShowCreateForm(false);
    setNewTagName('');
    setNewTagCategory('');
  };

  const handleAddTag = async (tag: Tag) => {
    if (selectedTags.find(t => t.id === tag.id)) return;

    try {
      await TagService.addTagsToContent(contentId, [tag.id]);
      onTagsChange([...selectedTags, tag]);
      // Remove from suggestions if present
      setSuggestions(prev => prev.filter(s => s.id !== tag.id));
    } catch (err) {
      setError('Failed to add tag');
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    try {
      await TagService.removeTagFromContent(contentId, tagId);
      onTagsChange(selectedTags.filter(t => t.id !== tagId));
    } catch (err) {
      setError('Failed to remove tag');
    }
  };

  const handleApplySuggestion = async (tag: Tag) => {
    await handleAddTag(tag);
  };

  const handleApplyAllSuggestions = async () => {
    if (suggestions.length === 0) return;

    try {
      const tagIds = suggestions.map(s => s.id);
      await TagService.addTagsToContent(contentId, tagIds);
      onTagsChange([...selectedTags, ...suggestions]);
      setSuggestions([]);
    } catch (err) {
      setError('Failed to apply suggestions');
    }
  };

  // Start creating a new tag
  const handleStartCreateTag = () => {
    setNewTagName(searchQuery);
    setNewTagCategory('');
    setShowCreateForm(true);
  };

  // Cancel creating a new tag
  const handleCancelCreateTag = () => {
    setShowCreateForm(false);
    setNewTagName('');
    setNewTagCategory('');
  };

  // Create and add a new tag
  const handleCreateTag = async () => {
    if (!newTagName.trim() || !newTagCategory) {
      setError('Please provide a tag name and category');
      return;
    }

    setCreatingTag(true);
    setError(null);

    try {
      // Create the tag
      const newTag = await TagService.createTag({
        name: newTagName.trim(),
        category: newTagCategory,
        color: CATEGORY_COLORS[newTagCategory] || '#6366f1',
      });

      // Add it to the content
      await TagService.addTagsToContent(contentId, [newTag.id]);
      onTagsChange([...selectedTags, newTag]);

      // Update local tag lists
      setAllTags(prev => [...prev, newTag]);
      setGroupedTags(prev => ({
        ...prev,
        [newTagCategory]: [...(prev[newTagCategory] || []), newTag],
      }));

      // Reset form
      setShowCreateForm(false);
      setNewTagName('');
      setNewTagCategory('');
      setSearchQuery('');
    } catch (err: any) {
      setError(err.message || 'Failed to create tag');
    } finally {
      setCreatingTag(false);
    }
  };

  // Filter tags based on search
  const filteredTags = searchQuery
    ? allTags.filter(
        tag =>
          tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          tag.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  // Get unselected tags for display
  const selectedIds = new Set(selectedTags.map(t => t.id));
  const availableTags = Object.entries(groupedTags).reduce((acc, [category, tags]) => {
    const available = tags.filter(t => !selectedIds.has(t.id));
    if (available.length > 0) {
      acc[category] = available;
    }
    return acc;
  }, {} as Record<string, Tag[]>);

  const open = Boolean(anchorEl);

  return (
    <Box>
      {/* Selected Tags Display */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        {selectedTags.map(tag => (
          <Chip
            key={tag.id}
            label={tag.name}
            size="small"
            onDelete={() => handleRemoveTag(tag.id)}
            sx={{
              bgcolor: tag.color + '20',
              borderColor: tag.color,
              border: '1px solid',
              '& .MuiChip-label': { color: tag.color },
              '& .MuiChip-deleteIcon': { color: tag.color },
            }}
          />
        ))}
        <Chip
          icon={<Add />}
          label={compact ? '' : 'Add Tag'}
          size="small"
          variant="outlined"
          onClick={handleOpenMenu}
          sx={{ cursor: 'pointer' }}
        />
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {/* Tag Selection Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: { width: 320, maxHeight: 480 },
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search tags..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : showCreateForm ? (
          // Inline Tag Creation Form
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Create New Tag
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Tag Name"
              value={newTagName}
              onChange={e => setNewTagName(e.target.value)}
              sx={{ mb: 2 }}
              autoFocus
            />
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={newTagCategory}
                label="Category"
                onChange={e => setNewTagCategory(e.target.value)}
              >
                {TAG_CATEGORIES.map(cat => (
                  <MenuItem key={cat} value={cat}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: CATEGORY_COLORS[cat],
                        }}
                      />
                      {cat}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={handleCancelCreateTag}
                disabled={creatingTag}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleCreateTag}
                disabled={creatingTag || !newTagName.trim() || !newTagCategory}
                startIcon={creatingTag ? <CircularProgress size={16} /> : <Add />}
              >
                Create & Add
              </Button>
            </Box>
          </Box>
        ) : searchQuery ? (
          // Search Results
          <List dense sx={{ maxHeight: 350, overflow: 'auto' }}>
            {filteredTags.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No tags match "{searchQuery}"
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddCircleOutline />}
                  onClick={handleStartCreateTag}
                >
                  Create "{searchQuery}" tag
                </Button>
              </Box>
            ) : (
              filteredTags.map(tag => (
                <ListItemButton
                  key={tag.id}
                  onClick={() => handleAddTag(tag)}
                  disabled={selectedIds.has(tag.id)}
                >
                  <Chip
                    size="small"
                    label={tag.name}
                    sx={{
                      bgcolor: tag.color + '20',
                      borderColor: tag.color,
                      border: '1px solid',
                      mr: 1,
                    }}
                  />
                  <ListItemText
                    secondary={tag.category}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              ))
            )}
          </List>
        ) : (
          // Categorized Tag List
          <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
            {/* AI Suggestions */}
            {showSuggestions && suggestions.length > 0 && (
              <>
                <ListSubheader
                  sx={{
                    bgcolor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <AutoAwesome fontSize="small" color="primary" />
                  <Typography variant="subtitle2">Suggested Tags</Typography>
                  <Button
                    size="small"
                    onClick={handleApplyAllSuggestions}
                    sx={{ ml: 'auto', textTransform: 'none' }}
                  >
                    Apply All
                  </Button>
                </ListSubheader>
                <Box sx={{ px: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {suggestions.map(tag => (
                    <Tooltip key={tag.id} title={`Based on ${tag.category.toLowerCase()}`}>
                      <Chip
                        size="small"
                        label={tag.name}
                        onClick={() => handleApplySuggestion(tag)}
                        icon={<AutoAwesome sx={{ fontSize: 14 }} />}
                        sx={{
                          bgcolor: tag.color + '20',
                          borderColor: tag.color,
                          border: '1px solid',
                          cursor: 'pointer',
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
                <Divider />
              </>
            )}

            {loadingSuggestions && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading suggestions...
                </Typography>
              </Box>
            )}

            {/* All Tags by Category */}
            {Object.entries(availableTags).map(([category, tags]) => (
              <React.Fragment key={category}>
                <ListSubheader
                  sx={{ bgcolor: 'background.paper', lineHeight: '36px' }}
                >
                  {category}
                </ListSubheader>
                <Box sx={{ px: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {tags.map(tag => (
                    <Chip
                      key={tag.id}
                      size="small"
                      label={tag.name}
                      onClick={() => handleAddTag(tag)}
                      sx={{
                        bgcolor: tag.color + '20',
                        borderColor: tag.color,
                        border: '1px solid',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: tag.color + '30',
                        },
                      }}
                    />
                  ))}
                </Box>
              </React.Fragment>
            ))}

            {Object.keys(availableTags).length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <LocalOffer sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  All tags have been applied
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Popover>
    </Box>
  );
}

// Compact version for displaying tags only (no editing)
export function TagDisplay({ tags }: { tags: Tag[] }) {
  if (tags.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {tags.map(tag => (
        <Chip
          key={tag.id}
          label={tag.name}
          size="small"
          sx={{
            bgcolor: tag.color + '20',
            borderColor: tag.color,
            border: '1px solid',
            fontSize: '0.7rem',
            height: 20,
          }}
        />
      ))}
    </Box>
  );
}

// Bulk Tag Selector for Content Library toolbar
interface BulkTagSelectorProps {
  selectedContentIds: string[];
  onComplete: () => void;
}

export function BulkTagSelector({ selectedContentIds, onComplete }: BulkTagSelectorProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [groupedTags, setGroupedTags] = useState<Record<string, Tag[]>>({});
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const loadTags = async () => {
      setLoading(true);
      try {
        const data = await TagService.getTags();
        setGroupedTags(data.grouped);
      } catch (err) {
        console.error('Failed to load tags');
      } finally {
        setLoading(false);
      }
    };
    loadTags();
  }, []);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedTagIds(new Set());
  };

  const handleToggleTag = (tagId: string) => {
    const newSelected = new Set(selectedTagIds);
    if (newSelected.has(tagId)) {
      newSelected.delete(tagId);
    } else {
      newSelected.add(tagId);
    }
    setSelectedTagIds(newSelected);
  };

  const handleApply = async () => {
    if (selectedTagIds.size === 0) return;

    setApplying(true);
    try {
      await TagService.bulkAddTags(selectedContentIds, Array.from(selectedTagIds));
      handleClose();
      onComplete();
    } catch (err) {
      console.error('Failed to apply tags');
    } finally {
      setApplying(false);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Add tags to selected">
        <Button
          size="small"
          color="inherit"
          onClick={handleOpen}
          startIcon={<LocalOffer />}
        >
          Tag
        </Button>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { width: 320, maxHeight: 480 } }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2">
            Add tags to {selectedContentIds.length} items
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Select tags to apply
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {Object.entries(groupedTags).map(([category, tags]) => (
              <React.Fragment key={category}>
                <ListSubheader sx={{ bgcolor: 'background.paper', lineHeight: '36px' }}>
                  {category}
                </ListSubheader>
                <Box sx={{ px: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {tags.map(tag => (
                    <Chip
                      key={tag.id}
                      size="small"
                      label={tag.name}
                      onClick={() => handleToggleTag(tag.id)}
                      sx={{
                        bgcolor: selectedTagIds.has(tag.id) ? tag.color : tag.color + '20',
                        borderColor: tag.color,
                        border: '1px solid',
                        cursor: 'pointer',
                        color: selectedTagIds.has(tag.id) ? 'white' : 'inherit',
                      }}
                    />
                  ))}
                </Box>
              </React.Fragment>
            ))}
          </Box>
        )}

        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} size="small">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={selectedTagIds.size === 0 || applying}
            size="small"
            sx={{ ml: 'auto' }}
          >
            {applying ? 'Applying...' : `Apply ${selectedTagIds.size} Tags`}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
